/**
 * Structural link expansion — one logical link → the task-to-task edges it
 * actually stands for.
 *
 * v1 does this inside `_formatLink` / `_getImplicitLinks` (links_common.js):
 * a link to/from a summary whose dates are derived from its children is N
 * implicit links, one per leaf descendant. Everything that reasons about the
 * link graph needs the same rule, so it lives here once:
 *
 *  - `graph/analysis_graph.ts` — slack / critical path / `findCycles()`;
 *  - `core/api.ts:findCycleIfAdded` — `gantt.isCircularLink` and the
 *    `onBeforeLinkAdd` / `onBeforeLinkUpdate` guard.
 *
 * Pure: depends only on the snapshot (task kind, `autoScheduling`, hierarchy).
 * It does NOT build a graph — the candidate-cycle check must stay cheap on
 * those latency-sensitive UI paths.
 *
 * Two rules deliberately live at the CALL SITE, not here, because the two
 * consumers disagree about them:
 *  - a link between a summary and its own descendant produces no relation in
 *    v1, but the analysis graph currently keeps the `descendant → summary`
 *    direction (the snapshot builder drops only `summary → descendant`, D-25),
 *    and removing it would change slack / critical-path output;
 *  - an explicit self-link is a cycle for cycle detection, while the analysis
 *    passes must not see a self-edge.
 * `findCycleIfAdded` applies the first before calling here; the analysis
 * builder skips self-edges after calling here.
 */

import type { Snapshot, NormalizedTaskID, LinkSnapshot } from "../types";

/** The structural projection of a link: identity + endpoints, no scheduling data. */
export type StructuralLinkEdge = Pick<LinkSnapshot, "id" | "source" | "target">;

/**
 * True for a summary whose dates aggregate from its descendants. Mirrors v1's
 * `isSummaryTask(t) && !isManuallyScheduledSummary(t)`: a project-type task
 * with `auto_scheduling: false` carries its own fixed dates and participates
 * as a regular endpoint.
 */
function isDerivedSummary(snapshot: Snapshot, id: NormalizedTaskID): boolean {
  const task = snapshot.tasks.get(id);
  return !!task && task.kind === "summary" && task.autoScheduling !== false;
}

/**
 * Leaf replicas of one link endpoint: the endpoint itself for a task that
 * carries its own dates, or the derived summary's non-derived descendants.
 * Falls back to the endpoint when a derived summary has no eligible
 * descendant (an empty project behaves like a plain task).
 */
export function leafReplicasOf(snapshot: Snapshot, id: NormalizedTaskID): NormalizedTaskID[] {
  if (!isDerivedSummary(snapshot, id)) return [id];
  const result: NormalizedTaskID[] = [];
  for (const desc of snapshot.hierarchy.descendantsOf(id)) {
    const descTask = snapshot.tasks.get(desc);
    if (!descTask || descTask.unscheduled) continue;
    if (isDerivedSummary(snapshot, desc)) continue; // skip nested derived summaries
    result.push(desc);
  }
  return result.length > 0 ? result : [id];
}

/**
 * Expand one link into the edges it stands for.
 *
 * Returns no edges when an endpoint is unknown or unscheduled (D-11; v1's
 * `links_builder` skips those relations too). Replica pairs that collapse onto
 * the same task are dropped — a leaf can't be its own predecessor — but an
 * explicit `source === target` link is preserved, so a caller doing cycle
 * detection sees the self-loop.
 */
export function expandStructuralLink(snapshot: Snapshot, link: StructuralLinkEdge): StructuralLinkEdge[] {
  const sourceTask = snapshot.tasks.get(link.source);
  const targetTask = snapshot.tasks.get(link.target);
  if (!sourceTask || !targetTask) return [];
  if (sourceTask.unscheduled || targetTask.unscheduled) return [];

  if (link.source === link.target) {
    return [{ id: link.id, source: link.source, target: link.target }];
  }

  const edges: StructuralLinkEdge[] = [];
  for (const s of leafReplicasOf(snapshot, link.source)) {
    for (const t of leafReplicasOf(snapshot, link.target)) {
      if (s === t) continue;
      edges.push({ id: link.id, source: s, target: t });
    }
  }
  return edges;
}

/**
 * True when the link connects a derived summary with one of its own
 * descendants (either direction). v1 `_formatLink` returns no relations for
 * that pair, so it can never be circular.
 */
export function isSummaryDescendantPair(
  snapshot: Snapshot,
  source: NormalizedTaskID,
  target: NormalizedTaskID
): boolean {
  const hierarchy = snapshot.hierarchy;
  return (
    (hierarchy.isSummary(target) && hierarchy.isDescendantOf(source, target)) ||
    (hierarchy.isSummary(source) && hierarchy.isDescendantOf(target, source))
  );
}

